import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '@/utils/authFetch';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Mail, Phone, Briefcase, Calendar, Trash2, Edit, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function HR() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    department: '',
    position: '',
    join_date: new Date().toISOString().split('T')[0]
  });

  const fetchData = useCallback(async () => {
    try {
      const [empRes, userRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/hr/employees`),
        authFetch(`${BACKEND_URL}/api/auth/me`)
      ]);

      if (userRes.ok) {
        const userData = await userRes.json();
        setCurrentUser(userData);

        if (userData.role !== 'admin') {
          toast.error('Access denied. Admin only.');
          navigate('/dashboard');
          return;
        }
      }

      if (empRes.ok) {
        const empData = await empRes.json();
        setEmployees(empData);
      }
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // First create the employee record
      const response = await authFetch(`${BACKEND_URL}/api/hr/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const employeeData = await response.json();
        
        // Also create a user login account for this employee
        // Generate username from email (part before @)
        const username = formData.email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
        const tempPassword = 'Welcome@123'; // Default password
        
        try {
          const userResponse = await authFetch(`${BACKEND_URL}/api/auth/create-user`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              username: username,
              password: tempPassword,
              email: formData.email,
              name: formData.name,
              role: 'employee'
            })
          });
          
          if (userResponse.ok) {
            toast.success(`Employee added! Login: ${username} / ${tempPassword}`);
          } else {
            // User might already exist, that's okay
            toast.success('Employee added successfully');
          }
        } catch (userError) {
          toast.success('Employee added successfully');
        }
        
        setDialogOpen(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          department: '',
          position: '',
          join_date: new Date().toISOString().split('T')[0]
        });
        fetchData();
      } else {
        toast.error('Failed to add employee');
      }
    } catch (error) {
      toast.error('Failed to add employee');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`${BACKEND_URL}/api/hr/employees/${selectedEmployee.employee_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Employee updated successfully');
        setEditDialogOpen(false);
        setSelectedEmployee(null);
        fetchData();
      } else {
        toast.error('Failed to update employee');
      }
    } catch (error) {
      toast.error('Failed to update employee');
    }
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;

    try {
      const response = await authFetch(`${BACKEND_URL}/api/hr/employees/${employeeId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Employee deleted');
        fetchData();
      } else {
        toast.error('Failed to delete employee');
      }
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  const openEditDialog = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      name: employee.name,
      email: employee.email,
      phone: employee.phone || '',
      department: employee.department || '',
      position: employee.position || '',
      join_date: employee.join_date
    });
    setEditDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      on_leave: 'bg-amber-50 text-amber-700 border-amber-100',
      inactive: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return styles[status] || styles.active;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="hr-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">HR Management</h1>
            <p className="text-slate-600 mt-1">Manage employee records and information</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-employee-button" className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Employee
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-employee-dialog">
              <DialogHeader>
                <DialogTitle>Add New Employee</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    data-testid="emp-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Enter full name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="emp-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="email@company.com"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    data-testid="emp-phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+91 1234567890"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      data-testid="emp-department-input"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      placeholder="e.g., Engineering"
                    />
                  </div>
                  <div>
                    <Label htmlFor="position">Position</Label>
                    <Input
                      id="position"
                      data-testid="emp-position-input"
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      placeholder="e.g., Software Engineer"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="join_date">Join Date *</Label>
                  <Input
                    id="join_date"
                    type="date"
                    data-testid="emp-joindate-input"
                    value={formData.join_date}
                    onChange={(e) => setFormData({ ...formData, join_date: e.target.value })}
                    required
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="submit-employee-button">Add Employee</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent data-testid="edit-employee-dialog">
            <DialogHeader>
              <DialogTitle>Edit Employee</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Full Name *</Label>
                <Input
                  id="edit-name"
                  data-testid="edit-emp-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email *</Label>
                <Input
                  id="edit-email"
                  type="email"
                  data-testid="edit-emp-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  data-testid="edit-emp-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-department">Department</Label>
                  <Input
                    id="edit-department"
                    data-testid="edit-emp-department-input"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-position">Position</Label>
                  <Input
                    id="edit-position"
                    data-testid="edit-emp-position-input"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="edit-join_date">Join Date *</Label>
                <Input
                  id="edit-join_date"
                  type="date"
                  data-testid="edit-emp-joindate-input"
                  value={formData.join_date}
                  onChange={(e) => setFormData({ ...formData, join_date: e.target.value })}
                  required
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="update-employee-button">Update Employee</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Employees List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {employees.map((employee) => (
            <Card key={employee.employee_id} className="card-hover" data-testid={`employee-${employee.employee_id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{employee.name}</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">{employee.position || 'No position'}</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(employee.status)}`}>
                    {employee.status}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-slate-600">
                    <Mail className="w-4 h-4 mr-2 text-slate-400" />
                    {employee.email}
                  </div>
                  {employee.phone && (
                    <div className="flex items-center text-sm text-slate-600">
                      <Phone className="w-4 h-4 mr-2 text-slate-400" />
                      {employee.phone}
                    </div>
                  )}
                  {employee.department && (
                    <div className="flex items-center text-sm text-slate-600">
                      <Briefcase className="w-4 h-4 mr-2 text-slate-400" />
                      {employee.department}
                    </div>
                  )}
                  <div className="flex items-center text-sm text-slate-600">
                    <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                    Joined: {employee.join_date}
                  </div>
                </div>
                <div className="flex space-x-2 pt-2 border-t border-slate-100">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`edit-emp-${employee.employee_id}`}
                    onClick={() => openEditDialog(employee)}
                    className="flex-1 text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`delete-emp-${employee.employee_id}`}
                    onClick={() => handleDelete(employee.employee_id)}
                    className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {employees.length === 0 && (
            <Card className="col-span-full">
              <CardContent className="py-12">
                <div className="text-center text-slate-500">
                  <UserCog className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                  <p>No employees added yet</p>
                  <p className="text-sm mt-2">Click "Add Employee" to add your first employee</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
